import { CountryCode } from "libphonenumber-js";
import { DayWithHours, WeeklySchedule } from "./agendaConfiguration";
import { DateTime } from "luxon";
import { normalizeSpecificDays, normalizeWeeklySchedule } from "@/utils/availability";
import { cloneDeep } from "lodash";

export type User = {
  id: number;
  userId: number;
  name: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  picture: string | undefined;
  description: string | undefined;
  stripeConnected?: boolean;
  defaultCountryCode?: string;
  bookingUrl: string;
  isOnSiteAllowed: boolean;
  isVirtualAllowed: boolean;
  locationDetails: string;
  weeklySchedule: WeeklySchedule;
  specificDays?: DayWithHours[];
  busyDays?: DayWithHours[];
  zoomUrl: string | null | undefined;
};

export type GoogleDateTime = {
  dateTime: string;
  timeZone: string;
};

export type GoogleDate = {
  date: string;
  timeZone: string;
};

export type GoogleCalendarEvent = {
  kind?: string;
  etag?: string;
  id?: string;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  htmlLink?: string;
  created?: string;
  updated?: string;
  summary?: string;
  description?: string;
  location?: string;
  colorId?: string;
  creator?: {
    id?: string;
    email?: string;
    displayName?: string;
    self?: boolean;
  };
  organizer?: {
    id?: string;
    email?: string;
    displayName?: string;
    self?: boolean;
  };
  start: GoogleDateTime | GoogleDate;
  end: GoogleDateTime | GoogleDate;
  endTimeUnspecified?: boolean;
  recurrence?: string[];
  recurringEventId?: string;
  originalStartTime?: {
    date?: string;
    dateTime?: string;
    timeZone?: string;
  };
  transparency?: 'opaque' | 'transparent'; // si bloquea o no bloquea horario
  visibility?: 'default' | 'public' | 'private' | 'confidential';
};

export interface UserMixin {
  clientTimezone?: string | undefined;
  fetch(path: string, init?: RequestInit): Promise<any>;
  getUser(extension?: string | undefined, userId?: string | undefined): Promise<User>;
  updateUser(user: Partial<User>): Promise<User>;
  getGoogleCalendarEvents(
    startDate: Date,
    endDate: Date,
    userId?: string | null,
    extension?: string | null
  ): Promise<GoogleCalendarEvent[]>;
  uploadProfilePicture: (imageFile: File) => Promise<any>;
}
export const UserMixin = {
  async fetch(path: string, init?: RequestInit): Promise<any> {
    throw new Error('Not implemented');
  },
  async getUser(extension?: string | undefined, userId?: string | undefined): Promise<User> {
    const user = await this.fetch(`/users${extension ? `?extension=${extension}` : userId ? `?userId=${userId}` : ""}`, {
      method: 'get',
    });
    const timeZone = this.clientTimezone
      ? this.clientTimezone
      : DateTime.local().zoneName;
    
    return {
      ...user,
      weeklySchedule: normalizeWeeklySchedule(user.weeklySchedule, 'UTC', timeZone),
      specificDays: normalizeSpecificDays(user.specificDays || [], 'UTC', timeZone),
      busyDays: normalizeSpecificDays(user.busyDays || [], 'UTC', timeZone),
    };
  },
  async updateUser(user: Partial<User> & {id: number}): Promise<User> {
    const timeZone = this.clientTimezone
      ? this.clientTimezone
      : DateTime.local().zoneName;
    const body = cloneDeep({ ...user });
    if (user.weeklySchedule) {
      body.weeklySchedule = normalizeWeeklySchedule(
        cloneDeep(user.weeklySchedule),
        timeZone,
        'UTC'
      );
    }
    if (user.specificDays) {
      body.specificDays = normalizeSpecificDays(
        cloneDeep(user.specificDays),
        timeZone,
        'UTC'
      );
    }
    if (user.busyDays) {
      body.busyDays = normalizeSpecificDays(
        cloneDeep(user.busyDays),
        timeZone,
        'UTC'
      );
    }

    const resUser = await this.fetch(`/users/${user.id}`, {
      method: 'put',
      body: JSON.stringify({
        ...body,
      }),
    });
    return {
      ...resUser,
      weeklySchedule: normalizeWeeklySchedule(resUser.weeklySchedule, 'UTC', timeZone),
      specificDays: normalizeSpecificDays(resUser.specificDays || [], 'UTC', timeZone),
      busyDays: normalizeSpecificDays(resUser.busyDays || [], 'UTC', timeZone),
    };
  },
  async getGoogleCalendarEvents(
    startDate: Date,
    endDate: Date,
    userId: string | null,
    extension: string | null = null
  ): Promise<GoogleCalendarEvent[]> {
    const res = await this.fetch(
      `/users/google/calendar?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}${userId ? `&user_id=${userId}` : extension ? `&extension=${extension}` : ''}`,
      { method: 'get' }
    );
    return res;
  },
  async uploadProfilePicture(imageFile: File): Promise<any> {
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onloadend = async () => {
        try {
          const base64String = reader.result?.toString().split(',')[1]; // Extraer Base64 sin encabezado
          if (!base64String) {
            throw new Error('No se pudo convertir la imagen a Base64.');
          }

          const response = await this.fetch('/users/upload_profile_picture', {
            method: 'POST',
            body: JSON.stringify({
              picture: base64String,
              filename: 'profile_picture.png',
              mimetype: imageFile.type,
            }),
          });

          resolve(response);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = reject;
      reader.readAsDataURL(imageFile); // Convertir imagen a Base64
    });
  },
} as UserMixin;
